import { useEffect, useMemo, useState } from 'react'
import type { GETObjectModel, ObjectFieldDTO } from '@types'
import { useFormContext } from 'react-hook-form'
import { AppWsNotification, MobileApp, Variable } from '@mobileTypes'
import { InternalIdInput } from '@library'
import { DataGrid, GridColDef } from '@microservices/wiskey-react-table-component'
import { Add as AddIcon, Code as CodeIcon } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { ScriptValueDialog, Variable as ScriptVariable, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { useFetchAllObjectQuery } from '@redux/api'
import { useFetchMultipleObjectsByCodeQuery, useFetchObjectByCodeQuery } from '@redux/api/objects.api'
import {
  buildItemVariablesFromExpands,
  collectMissingModelCodesAlongPaths,
  parseExpandsList,
} from '../../../utils/pushNotificationExpandHints'

import { COMMON_SCRIPT_VARIABLES } from '@mobileConstants'
import { useMobileGlobalVariables } from '@mobileHooks'
import { generateDataGridActionsColumn } from '@helpers'

const EVENT_NAME_OPTIONS = ['Created', 'Updated', 'Deleted']
const DEFAULT_CHANNEL_ID = 'ws-events-v2'

const EXPANDS_PLACEHOLDER = 'object_ref:object_ref,other_object_ref.obj:other_object_ref.obj'

const WS_DATA_VARIABLE: ScriptVariable = {
  name: 'wsData',
  type: [
    { name: 'date', type: 'string', description: 'ISO timestamp of the event' },
    { name: 'objectId', type: 'string', description: 'ID of the changed object' },
    { name: 'objectCode', type: 'string', description: 'Code of the source object model' },
    { name: 'eventName', type: 'string', description: 'Event type: Created, Updated, Deleted' },
    {
      name: 'changes',
      description: 'Diff of the Updated event (empty for Created/Deleted)',
      type: [
        {
          name: 'record',
          description: 'Array of field changes: [{ field, oldValue, newValue }]',
          type: 'object[]',
        },
      ],
    },
  ],
  description: 'WebSocket message payload (new socket format)',
}

function buildChangedFieldsVariable(itemFields: ScriptVariable[], eventName: string): ScriptVariable | null {
  if (eventName !== 'Updated') {
    return null
  }

  return {
    name: 'changedFields',
    type: itemFields.length
      ? itemFields.map(field => ({
          name: field.name,
          description: field.description,
          type: [
            {
              name: 'oldValue',
              type: 'string',
              description: 'Previous field value',
            },
            {
              name: 'newValue',
              type: 'string',
              description: 'Current field value',
            },
          ],
        }))
      : 'object',
    description:
      'Shortcut built from wsData.changes.record: { [field]: { oldValue, newValue } }. Empty object for Created/Deleted.',
  }
}

type ActiveEditor = 'filter' | 'titleJs' | 'messageJs' | null

interface NotificationDialogProps {
  open: boolean
  item: AppWsNotification
  onClose: () => void
  onSave: (item: AppWsNotification) => void
}

const deletedEventHint = `For Deleted events the app does not load the object (it no longer exists). 'item' is undefined
Use 'globalVariables' if you saved the object there earlier`

const NotificationDialog = ({ open, item, onClose, onSave }: NotificationDialogProps) => {
  const { data: objectsResponse, isLoading: isLoadingObjects } = useFetchAllObjectQuery()
  const { variables: globalVariables = [] } = useMobileGlobalVariables()

  const [internalId, setInternalId] = useState(item.internalId)
  const [objectCode, setObjectCode] = useState(item.objectCode)
  const [eventName, setEventName] = useState(item.eventName)
  const [filter, setFilter] = useState(item.filter || '')
  const [expands, setExpands] = useState(item.expands || '')
  const [titleJs, setTitleJs] = useState(item.titleJs)
  const [messageJs, setMessageJs] = useState(item.messageJs || '')
  const [channelId] = useState(item.channelId || DEFAULT_CHANNEL_ID)
  const [playSound, setPlaySound] = useState(item.playSound !== false)
  const [errors, setErrors] = useState<{ internalId?: string; objectCode?: string; eventName?: string; titleJs?: string }>({})
  const [activeEditor, setActiveEditor] = useState<ActiveEditor>(null)
  const [extraLinkedModels, setExtraLinkedModels] = useState<GETObjectModel[]>([])

  const { data: objectModel } = useFetchObjectByCodeQuery(objectCode, { skip: !objectCode })

  const parsedExpandPaths = useMemo(() => parseExpandsList(expands), [expands])

  const modelsMap = useMemo(() => {
    const m = new Map<string, ObjectFieldDTO[]>()
    if (objectCode && objectModel?.fields) m.set(objectCode, objectModel.fields)
    for (const model of extraLinkedModels) {
      m.set(model.code, model.fields)
    }
    return m
  }, [objectCode, objectModel, extraLinkedModels])

  const missingLinkedModelCodes = useMemo(() => {
    if (!objectCode || !parsedExpandPaths.length) return []
    return collectMissingModelCodesAlongPaths(parsedExpandPaths, objectCode, modelsMap)
  }, [parsedExpandPaths, objectCode, modelsMap])

  const { data: linkedModelsBatch = [] } = useFetchMultipleObjectsByCodeQuery(missingLinkedModelCodes, {
    skip: !objectCode || missingLinkedModelCodes.length === 0,
  })

  useEffect(() => {
    if (!objectCode) setExtraLinkedModels([])
  }, [objectCode])

  useEffect(() => {
    if (linkedModelsBatch.length) {
      setExtraLinkedModels(prev => {
        const byCode = new Map(prev.map(mod => [mod.code, mod]))
        for (const mod of linkedModelsBatch) {
          byCode.set(mod.code, mod)
        }
        return [...byCode.values()]
      })
    }
  }, [linkedModelsBatch])

  const itemFields = useMemo(
    () => buildItemVariablesFromExpands(objectModel?.fields ?? [], parsedExpandPaths, modelsMap),
    [objectModel, parsedExpandPaths, modelsMap]
  )

  const changedFieldsVariable = useMemo<ScriptVariable | null>(
    () => buildChangedFieldsVariable(itemFields, eventName),
    [itemFields, eventName]
  )

  const globalVariablesScriptVariable = useMemo<ScriptVariable>(
    () => ({
      name: 'globalVariables',
      type: globalVariables.length
        ? globalVariables.map((variable: Variable) => ({
            name: variable.internalId,
            type: variable.valueType || 'any',
            description: `Global variable: ${variable.internalId}`,
          }))
        : 'any',
      description: 'App global variables. Configure under Mobile Global Variables.',
    }),
    [globalVariables]
  )

  const configuredGlobalVariableIds = useMemo(
    () => globalVariables.map((v: Variable) => v.internalId).filter(Boolean),
    [globalVariables]
  )

  const wsFilterScriptVariables = useMemo<ScriptVariable[]>(
    () =>
      [WS_DATA_VARIABLE, changedFieldsVariable, globalVariablesScriptVariable, ...COMMON_SCRIPT_VARIABLES].filter(
        Boolean
      ) as ScriptVariable[],
    [changedFieldsVariable, globalVariablesScriptVariable]
  )

  const wsFilterFieldsInfo = useMemo(
    () => variablesToFieldsInfo(wsFilterScriptVariables),
    [wsFilterScriptVariables]
  )

  const wsJsScriptVariables = useMemo<ScriptVariable[]>(() => {
    const itemDescription =
      eventName === 'Deleted'
        ? 'Undefined for Deleted (record removed). Use globalVariables if you stored a copy earlier. Field list below is the model shape for reference only.'
        : objectCode
          ? `Fields of ${objectCode} object (expanded paths from Expands when set)`
          : 'Object fetched from API by objectId'
    return [
      WS_DATA_VARIABLE,
      changedFieldsVariable,
      {
        name: 'item',
        type: itemFields.length ? itemFields : 'object',
        description: itemDescription,
      },
      globalVariablesScriptVariable,
      ...COMMON_SCRIPT_VARIABLES,
    ].filter(Boolean) as ScriptVariable[]
  }, [objectCode, eventName, itemFields, changedFieldsVariable, globalVariablesScriptVariable])

  const wsJsFieldsInfo = useMemo(() => variablesToFieldsInfo(wsJsScriptVariables), [wsJsScriptVariables])

  useEffect(() => {
    setInternalId(item.internalId)
    setObjectCode(item.objectCode)
    setEventName(item.eventName)
    setFilter(item.filter || '')
    setExpands(item.expands || '')
    setTitleJs(item.titleJs)
    setMessageJs(item.messageJs || '')
    setPlaySound(item.playSound !== false)
    setExtraLinkedModels([])
    setErrors({})
  }, [item])

  const validate = () => {
    const newErrors: typeof errors = {}
    if (!internalId.trim()) newErrors.internalId = 'Required'
    if (!objectCode) newErrors.objectCode = 'Required'
    if (!eventName) newErrors.eventName = 'Required'
    if (!titleJs.trim()) newErrors.titleJs = 'Required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) return
    onSave({
      internalId: internalId.trim(),
      objectCode,
      eventName,
      filter: filter.trim() || undefined,
      expands: expands.trim() || undefined,
      titleJs: titleJs.trim(),
      messageJs: messageJs.trim() || undefined,
      channelId: channelId.trim() || DEFAULT_CHANNEL_ID,
      playSound,
    })
  }

  const selectedObject = objectsResponse?.find(obj => obj.code === objectCode) ?? null

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>{item.internalId ? 'Edit Push Notification' : 'Add Push Notification'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <InternalIdInput
                fullWidth
                label='Internal ID'
                value={internalId}
                onChange={value => {
                  setInternalId(value)
                  setErrors(prev => ({ ...prev, internalId: undefined }))
                }}
                error={!!errors.internalId}
                helperText={errors.internalId || 'Unique notification identifier'}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={objectsResponse || []}
                getOptionLabel={option =>
                  typeof option === 'string' ? option : `${option.title} (${option.code})`
                }
                value={selectedObject}
                onChange={(_, data) => {
                  setObjectCode(data?.code || '')
                  setErrors(prev => ({ ...prev, objectCode: undefined }))
                }}
                loading={isLoadingObjects}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Source Object'
                    error={!!errors.objectCode}
                    helperText={errors.objectCode || 'Object to subscribe on WebSocket'}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingObjects ? <CircularProgress color='inherit' size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Event Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label='Event Name'
                value={eventName}
                onChange={e => {
                  setEventName(e.target.value)
                  setErrors(prev => ({ ...prev, eventName: undefined }))
                }}
                error={!!errors.eventName}
                helperText={errors.eventName || 'WS event type to handle'}
                required
                SelectProps={{ displayEmpty: true }}
              >
                {EVENT_NAME_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {eventName === 'Deleted' && (
              <Grid item xs={12}>
                <Alert severity='warning' sx={{ '& .MuiAlert-message': { whiteSpace: 'pre-line' } }}>
                  {deletedEventHint}
                </Alert>
              </Grid>
            )}

            {/* Filter JS */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='RSQL Filter JS (optional)'
                value={filter}
                onClick={() => eventName !== 'Deleted' && setActiveEditor('filter')}
                inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                placeholder='return `status==ACTIVE`'
                disabled={eventName === 'Deleted'}
                helperText={
                  eventName === 'Deleted'
                    ? 'Not used for Deleted events (no object load).'
                    : 'JS returning an RSQL filter. Return false to skip the API request.'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        disabled={eventName === 'Deleted'}
                        size='small'
                        onClick={e => {
                          e.stopPropagation()
                          setActiveEditor('filter')
                        }}
                      >
                        <CodeIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Expands (optional)'
                value={expands}
                onChange={e => setExpands(e.target.value)}
                placeholder={EXPANDS_PLACEHOLDER}
                disabled={eventName === 'Deleted'}
                helperText={
                  eventName === 'Deleted'
                    ? 'Not used for Deleted events (no object load).'
                    : `Comma-separated paths (nested refs and nested-of-nested). Example: ${EXPANDS_PLACEHOLDER}`
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Title JS'
                value={titleJs}
                onClick={() => setActiveEditor('titleJs')}
                inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                placeholder="return item?.name ?? 'New event'"
                error={!!errors.titleJs}
                helperText={
                  errors.titleJs || 'JS returning notification title (wsData, item, changedFields, globalVariables)'
                }
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={e => {
                          e.stopPropagation()
                          setErrors(prev => ({ ...prev, titleJs: undefined }))
                          setActiveEditor('titleJs')
                        }}
                      >
                        <CodeIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Message JS (optional)'
                value={messageJs}
                onClick={() => setActiveEditor('messageJs')}
                inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                placeholder='return item?.description'
                helperText='Optional JS for notification body (wsData, item, changedFields, globalVariables)'
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={e => {
                          e.stopPropagation()
                          setActiveEditor('messageJs')
                        }}
                      >
                        <CodeIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox checked={playSound} onChange={e => setPlaySound(e.target.checked)} />
                }
                label='Play Sound'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant='contained' onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {activeEditor === 'filter' && (
        <ScriptValueDialog
          isShow
          value={filter}
          title='Edit RSQL Filter JS'
          hint={
            eventName === 'Deleted'
              ? 'Not applied for Deleted events (the object is not loaded via API).'
              : 'Return an RSQL filter string (e.g. `return \`status==ACTIVE\``). The _id filter for this object is added automatically. Return false to skip the API request entirely. Returning undefined skips only the extra filter; the object is still loaded by _id.'
          }
          language='js'
          variables={wsFilterScriptVariables}
          fieldsInfo={wsFilterFieldsInfo}
          onClose={() => setActiveEditor(null)}
          onSave={code => {
            setFilter(code)
            setActiveEditor(null)
          }}
          examples={[
            { title: 'Filter by static field', code: "return 'status==ACTIVE'" },
            { title: 'Multiple conditions', code: "return 'status==ACTIVE;type==ORDER'" },
            {
              title: 'Only when field changed (changedFields)',
              code: "if (changedFields?.status === undefined) return undefined; return `status==${changedFields.status.newValue}`",
            },
            {
              title: 'Skip request when data is missing',
              code: "const shiftId = String(globalVariables.current_shift_id || ''); if (!shiftId) return false; return `shift_assigned_to_ref==${shiftId}`",
            },
          ]}
        />
      )}

      {activeEditor === 'titleJs' && (
        <ScriptValueDialog
          isShow
          value={titleJs}
          title='Edit Title JS'
          hint={
            eventName === 'Deleted'
              ? `Return a string for the notification title. ${deletedEventHint}`
              : 'Return a string for the notification title. Return `null` / empty string to suppress the notification. Uses wsData, item, changedFields, globalVariables.'
          }
          language='js'
          variables={wsJsScriptVariables}
          fieldsInfo={wsJsFieldsInfo}
          onClose={() => setActiveEditor(null)}
          onSave={code => {
            setTitleJs(code)
            setErrors(prev => ({ ...prev, titleJs: undefined }))
            setActiveEditor(null)
          }}
          examples={[
            { title: 'Object name', code: "return item?.name ?? 'New event'" },
            { title: 'With event type', code: 'return `${wsData.eventName}: ${item?.title}`' },
            {
              title: 'Fire only if status changed',
              code: "if (changedFields?.status === undefined) return null; return `Status: ${changedFields.status.newValue}`",
            },
            {
              title: 'Fire only if specific fields changed',
              code: "const startChanged = changedFields?.start_time !== undefined; const endChanged = changedFields?.end_time !== undefined; return startChanged && endChanged ? 'Task time changed' : null;",
            },
            {
              title: 'Old → new value',
              code: "const c = changedFields?.status; return c ? `Status: ${c.oldValue} → ${c.newValue}` : null;",
            },
            {
              title: 'From global variable (e.g. Deleted)',
              code: "return globalVariables?.lastTaskTitle ?? wsData.objectId",
            },
          ]}
        />
      )}

      {activeEditor === 'messageJs' && (
        <ScriptValueDialog
          isShow
          value={messageJs}
          title='Edit Message JS'
          hint={
            eventName === 'Deleted'
              ? `Return a string for the notification body (optional). ${deletedEventHint}`
              : 'Return a string for the notification body (optional). Uses wsData, item, changedFields, globalVariables.'
          }
          language='js'
          variables={wsJsScriptVariables}
          fieldsInfo={wsJsFieldsInfo}
          onClose={() => setActiveEditor(null)}
          onSave={code => {
            setMessageJs(code)
            setActiveEditor(null)
          }}
          examples={[
            { title: 'Object description', code: 'return item?.description' },
            { title: 'Status with date', code: 'return `Status: ${item?.status} at ${moment().format("HH:mm")}`' },
            {
              title: 'List of changed fields',
              code: "return Object.keys(changedFields || {}).join(', ')",
            },
            {
              title: 'Field diff',
              code: "const c = changedFields?.start_time; return c ? `Start: ${moment(c.oldValue).format('HH:mm')} → ${moment(c.newValue).format('HH:mm')}` : '';",
            },
            { title: 'From global variable', code: "return globalVariables?.lastDeletedTitle ?? ''" },
          ]}
        />
      )}
    </>
  )
}

export const PushNotificationsConfigTab = () => {
  const { watch, setValue } = useFormContext<MobileApp>()
  const wsNotifications = watch('wsNotifications') || []

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingItem, setEditingItem] = useState<AppWsNotification | null>(null)

  const handleAdd = () => {
    setEditingIndex(null)
    setEditingItem({
      internalId: '',
      objectCode: '',
      eventName: 'Created',
      titleJs: '',
      playSound: true,
    })
    setDialogOpen(true)
  }

  const handleEdit = (index: number) => {
    setEditingIndex(index)
    setEditingItem({ ...wsNotifications[index] })
    setDialogOpen(true)
  }

  const handleDelete = (index: number) => {
    setValue('wsNotifications', wsNotifications.filter((_, i) => i !== index), { shouldDirty: true })
  }

  const handleSave = (item: AppWsNotification) => {
    if (editingIndex !== null) {
      const updated = [...wsNotifications]
      updated[editingIndex] = item
      setValue('wsNotifications', updated, { shouldDirty: true })
    } else {
      setValue('wsNotifications', [...wsNotifications, item], { shouldDirty: true })
    }
    setDialogOpen(false)
  }

  const rows = useMemo(
    () =>
      wsNotifications.map((item, index) => ({
        ...item,
        id: index,
      })),
    [wsNotifications]
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'objectCode',
        header: 'Object Code',
        size: 160,
      },
      {
        field: 'eventName',
        header: 'Event',
        size: 110,
      },
      {
        field: 'filter',
        header: 'Filter JS',
        flex: 1,
        size: 200,
        renderCell: params => {
          const v = params.value as string | undefined
          return v ? (
            <Typography variant='body2' noWrap title={v} sx={{ fontFamily: 'monospace' }}>
              {v}
            </Typography>
          ) : null
        },
      },
      {
        field: 'expands',
        header: 'Expands',
        size: 160,
        renderCell: params => {
          const v = params.value as string | undefined
          return v ? (
            <Typography variant='body2' noWrap title={v} sx={{ fontFamily: 'monospace' }}>
              {v}
            </Typography>
          ) : (
            '-'
          )
        },
      },
      {
        field: 'titleJs',
        header: 'Title JS',
        flex: 1,
        size: 200,
        renderCell: params => {
          const v = params.value as string | undefined
          return v ? (
            <Typography variant='body2' noWrap title={v} sx={{ fontFamily: 'monospace' }}>
              {v}
            </Typography>
          ) : null
        },
      },
      generateDataGridActionsColumn({
        onEdit: params => handleEdit(Number(params.id)),
        onDelete: params => handleDelete(Number(params.id)),
      }),
    ],
    []
  )

  return (
    <>
      <Grid container flexDirection='column' spacing={1}>
        <Grid container item alignItems='center' sx={{ pl: 0 }}>
          <Grid item>
            <Button variant='contained' startIcon={<AddIcon />} onClick={handleAdd}>
              Add Notification
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          <DataGrid rows={rows} columns={columns} hideFooter disableRowSelectionOnClick />
        </Grid>
      </Grid>

      {dialogOpen && editingItem && (
        <NotificationDialog
          open={dialogOpen}
          item={editingItem}
          onClose={() => setDialogOpen(false)}
          onSave={handleSave}
        />
      )}
    </>
  )
}
